import React from 'react';
import { Chart } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { toast } from 'react-hot-toast';
import styles from './AssetBaseline.module.css';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

// Custom loading spinner component
const LoadingSpinner = () => (
    <div className={styles.loading_spinner_container}>
        <div className={styles.loading_spinner}></div>
    </div>
);

// Alert category → short label, and severity → colour (readable on the light modal).
const CATEGORY_LABEL = { geo: 'Geo', soc: 'SOC', ids: 'IDS', ot: 'OT', ioc: 'IOC', general: 'General' };
const catLabel = (c) => CATEGORY_LABEL[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : 'Alert');
const SEV_COLOR = { critical: '#c81212', high: '#c81212', medium: '#b45309', low: '#0f9d58', info: '#6d6d6d' };

const PortBaselineModal = ({ 
    isOpen, 
    onClose, 
    deviceIP, 
    watcherId 
}) => {
    const [loading, setLoading] = React.useState(false);
    const [portData, setPortData] = React.useState(null);
    const chartRef = React.useRef(null);
    const [barData, setBarData] = React.useState(null);
    const [hourlyData, setHourlyData] = React.useState(null);
    const [showAllAlerts, setShowAllAlerts] = React.useState(false);

    const fetchPortBaseline = React.useCallback(async () => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('deviceIP', deviceIP);
            formData.append('watcherId', watcherId);

            const response = await fetch('/api/port-baseline', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();
            
            if (data && data.status) {
                setPortData(data);
            } else {
                toast.error(data.msg || 'Failed to fetch port baseline data');
            }
        } catch (error) {
            console.error('Error fetching port baseline:', error);
            toast.error('Error fetching port baseline data');
        } finally {
            setLoading(false);
        }
    }, [deviceIP, watcherId]);

    React.useEffect(() => {
        if (isOpen && deviceIP) {
            fetchPortBaseline();
        }
    }, [isOpen, deviceIP, fetchPortBaseline]);

    // Update barChartOptions to match Dashboard styling
    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        layout: {
            padding: {
                top: 10,
                right: 60, // Sufficient padding for data labels
                bottom: 10,
                left: 10
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                display: false, // Hide x-axis like Dashboard
                grid: {
                    drawOnChartArea: false, // Clean look like Dashboard
                },
                ticks: {
                    font: {
                        size: 8,
                    }
                }
            },
            y: {
                display: true,
                grid: {
                    drawOnChartArea: false, // Clean look like Dashboard
                },
                ticks: {
                    color: '#ffffff',
                    font: {
                        size: 9,
                        family: "'Poppins', sans-serif",
                    },
                    maxRotation: 0,
                    minRotation: 0,
                }
            }
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: `Destination Ports for - ${deviceIP}`,
                color: '#ffffff',
                font: {
                    size: 16,
                    weight: 'bold',
                    family: "'Poppins', sans-serif",
                }
            },
            datalabels: {
                display: true,
                anchor: 'end',
                align: 'end',
                offset: 4,
                color: '#fff',
                font: {
                    size: 10,
                    weight: 600,
                    family: "'Poppins', sans-serif",
                },
                formatter: (value) => {
                    return value.toLocaleString(); // Format numbers with commas
                }
            }
        },
    };

    // Vertical bars, dark legible text on the light modal, no per-bar labels
    // (24 bars would be cluttered) — the tooltip carries the value.
    const hourlyChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: { drawOnChartArea: false },
                ticks: { color: '#ffffff', font: { size: 9, family: "'Poppins', sans-serif" }, maxRotation: 0, minRotation: 0 },
            },
            y: {
                beginAtZero: true,
                grid: { color: 'rgba(255,255,255,0.12)' },
                title: { display: true, text: hourlyData ? hourlyData.unit : 'B', color: '#ffffff', font: { size: 10 } },
                ticks: { color: '#ffffff', font: { size: 9, family: "'Poppins', sans-serif" } },
            },
        },
        plugins: {
            legend: { display: false },
            title: {
                display: true,
                text: `Network Traffic — Last 24 Hours (per hour) · ${deviceIP}`,
                color: '#ffffff',
                font: { size: 14, weight: 'bold', family: "'Poppins', sans-serif" },
            },
            datalabels: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` ${ctx.parsed.y} ${hourlyData ? hourlyData.unit : 'B'}`,
                },
            },
        },
    };

    // Update the chart data creation to fix the data structure issue
    React.useEffect(() => {
        if (portData && portData.portBaseline && portData.portBaseline.length > 0) {
            const top15Ports = portData.portBaseline.slice(0, 15);
            const labels = top15Ports.map(port => `${port.port} (${port.service || 'Unknown'})`);
            const data = top15Ports.map(port => port.frequency);
            
            setBarData({
                labels,
                datasets: [{
                    type: 'bar',
                    fill: true,
                    label: 'Connection Count',
                    data,
                    backgroundColor: '#36A2EB', // Simple blue color for now
                    borderColor: 'rgba(230, 111, 0, 1)',
                    borderWidth: 0,
                    borderRadius: 8,
                }],
            });
        } else {
            setBarData(null);
        }
    }, [portData]);

    // 24-hour hourly traffic. Pick a byte unit from the busiest hour so the
    // y-axis reads cleanly (B / KB / MB / GB), and keep all 24 buckets so quiet
    // hours show as zero.
    React.useEffect(() => {
        const ht = portData && Array.isArray(portData.hourlyTraffic) ? portData.hourlyTraffic : [];
        if (ht.length > 0) {
            const maxBytes = ht.reduce((m, h) => Math.max(m, h.bytes || 0), 0);
            const UNITS = [[1073741824, 'GB'], [1048576, 'MB'], [1024, 'KB'], [1, 'B']];
            const [div, unit] = UNITS.find(([d]) => maxBytes >= d) || [1, 'B'];
            // Label each bucket in the viewer's LOCAL time from the ISO ts the
            // backend sends (the ts is UTC; h.hour is a UTC fallback).
            const localHour = (h) => {
                const d = new Date(h.ts);
                return isNaN(d.getTime()) ? h.hour : `${String(d.getHours()).padStart(2, '0')}:00`;
            };
            setHourlyData({
                unit,
                data: {
                    labels: ht.map(localHour),
                    datasets: [{
                        type: 'bar',
                        label: `Traffic (${unit})`,
                        data: ht.map(h => +(((h.bytes || 0) / div).toFixed(2))),
                        backgroundColor: '#e11d2a',
                        borderWidth: 0,
                        borderRadius: 4,
                    }],
                },
            });
        } else {
            setHourlyData(null);
        }
    }, [portData]);

    // Remove the gradient effect useEffect (lines 291-308)
    // Remove lines 291-308 completely

    if (!isOpen) return null;

    return (
        <div className={styles.modal_overlay} onClick={onClose}>
            <div className={styles.modal_content_compact} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modal_header_compact}>
                    <div className={styles.header_left}>
                        <h2>Asset Baseline</h2>
                    </div>
                    <button 
                        className={styles.modal_close_button} 
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <div className={styles.modal_body_compact}>
                    {loading ? (
                        <LoadingSpinner />
                    ) : portData ? (
                        <>
                            <div className={styles.baseline_columns}>
                                {/* Left: top destination services */}
                                <div className={styles.baseline_col}>
                                    <h4 className={styles.baseline_col_title}>Top 15 Destination Services</h4>
                                    {barData && barData.labels.length > 0 ? (
                                        <div className={styles.chart_container_large}>
                                            <Chart
                                                ref={chartRef}
                                                type='bar'
                                                data={barData}
                                                options={barChartOptions}
                                            />
                                        </div>
                                    ) : (
                                        <div className={styles.no_data_container}>
                                            <p>No service usage recorded for this device yet.</p>
                                        </div>
                                    )}
                                </div>

                                {/* Right: top destination IPs — who this asset talks to */}
                                <div className={styles.baseline_col}>
                                    <h4 className={styles.baseline_col_title}>Top 15 Destination IPs</h4>
                                    {portData.destinations && portData.destinations.length > 0 ? (
                                        <table className={styles.dest_table}>
                                            <thead>
                                                <tr><th>Destination</th><th>Country</th><th>Conns</th></tr>
                                            </thead>
                                            <tbody>
                                                {portData.destinations.map((d, i) => (
                                                    <tr key={i}>
                                                        <td title={d.ip}>{d.host || d.ip}</td>
                                                        <td>{d.country || '—'}</td>
                                                        <td>{d.frequency}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className={styles.no_data_container}>
                                            <p>No destinations recorded for this device yet.</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Full-width: 24-hour hourly traffic baseline */}
                            <div className={styles.hourly_section}>
                                {hourlyData ? (
                                    <div className={styles.hourly_chart_container}>
                                        <Chart type='bar' data={hourlyData.data} options={hourlyChartOptions} />
                                    </div>
                                ) : (
                                    <div className={styles.no_data_container}>
                                        <p>No hourly traffic recorded for this device in the last 24 hours.</p>
                                    </div>
                                )}
                            </div>

                            {/* Compact alert context tied to this device */}
                            {portData.alerts && (
                                <div className={styles.alerts_section}>
                                    <div className={styles.alerts_heading}>
                                        <span>Alerts <span className={styles.alerts_window}>(last {portData.analysisWindow || '30'}d)</span></span>
                                        {portData.alerts.total > 0 && (
                                            <span className={styles.alerts_summary}>
                                                {portData.alerts.total} total{portData.alerts.open ? ` · ${portData.alerts.open} open` : ' · all acknowledged'} · {portData.alerts.byCategory.map(bc => `${bc.count} ${catLabel(bc.category)}`).join(' · ')}
                                            </span>
                                        )}
                                    </div>
                                    {portData.alerts.total === 0 ? (
                                        <p className={styles.alerts_empty}>No alerts tied to this device in the last {portData.analysisWindow || '30'} days.</p>
                                    ) : (
                                        <>
                                            <table className={styles.alerts_table}>
                                                <tbody>
                                                    {(showAllAlerts ? portData.alerts.clusters : portData.alerts.clusters.slice(0, 5)).map((a, i) => (
                                                        <tr key={i}>
                                                            <td className={styles.alert_sev} style={{ color: SEV_COLOR[a.severity] || '#6d6d6d' }}>{a.severity}</td>
                                                            <td className={styles.alert_cat}>{catLabel(a.category)}</td>
                                                            <td className={styles.alert_msg} title={a.message}>{a.message}</td>
                                                            <td className={styles.alert_cnt}>{a.count > 1 ? `×${a.count}` : ''}</td>
                                                            <td className={styles.alert_ack}>
                                                                {a.acknowledged
                                                                    ? <span style={{ color: '#0f9d58' }}>✓ Ack</span>
                                                                    : <span style={{ color: '#b45309' }}>Open{a.openCount > 1 ? ` (${a.openCount})` : ''}</span>}
                                                            </td>
                                                            <td className={styles.alert_time}>{a.lastSeen}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {portData.alerts.clusters.length > 5 && (
                                                <button type="button" className={styles.alerts_toggle} onClick={() => setShowAllAlerts(v => !v)}>
                                                    {showAllAlerts ? 'Show less' : `Show all ${portData.alerts.clusters.length}`}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Compact Summary */}
                            {portData.totalConnections ? (
                                <div className={styles.baseline_summary_compact}>
                                    <strong>{portData.totalConnections}</strong> connections across <strong>{portData.uniquePorts || 0}</strong> services and <strong>{portData.uniqueDestinations || 0}</strong> destinations over the last <strong>{portData.analysisWindow || '30'}</strong> days
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div className={styles.no_data_container}>
                            <h3>No Asset Baseline Available</h3>
                            <p>
                                Unable to retrieve assetbaseline for: <strong>{deviceIP}</strong>
                                <br />
                                This device may be new or has minimal network activity to establish a baseline.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PortBaselineModal;
